import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const domTreeVisualizationCode = `\u003c!-- The structure as perceived by the Browser Engine --\u003e
\u003chtml\u003e
  \u003chead\u003e
    \u003ctitle\u003eArchitecture Guide\u003c/title\u003e
  \u003c/head\u003e
  \u003cbody\u003e
    \u003cmain\u003e
      \u003ch1\u003eDOM Internals\u003c/h1\u003e
      \u003cp\u003eUnderstanding nodes vs elements.\u003c/p\u003e
    \u003c/main\u003e
  \u003c/body\u003e
\u003c/html\u003e

// The internal C++ representation is projected as a JS Object Tree:
// Document (Node)
//             main (Element)
//                  h1 (Element) -&gt; "DOM Internals" (TextNode)
//                  p (Element) -&gt; "Understanding..." (TextNode)`;

const nodeTypesCode = `// Identifying different archetypes in the DOM tree

const mainElement = document.querySelector('main');

// 1. ELEMENT_NODE (Type 1)
console.log(mainElement.nodeType === Node.ELEMENT_NODE); // true

// 2. TEXT_NODE (Type 3) - includes whitespace and newlines!
const firstText = mainElement.firstChild; 
console.log(firstText.nodeType); // 3

// 3. COMMENT_NODE (Type 8)
// 4. DOCUMENT_NODE (Type 9) - The root 'document' object itself

// Crucial: tagName vs nodeName
console.log(mainElement.tagName);  // "MAIN" (Always uppercase for HTML)
console.log(firstText.nodeName);   // "#text"`;

const documentPropertiesCode = `// Exploring the Global Document Interface

console.log(document.URL);           // Read-only location
console.log(document.title);         // Can be both READ and WRITTEN
console.log(document.doctype);       // The <!DOCTYPE html> node

// Live Collections (Legacy but important)
const allImages = document.images;   // HTMLCollection
const allForms = document.forms;     // Automatically updates when DOM changes

// ReadyState Management
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        console.log("Initial HTML parsed, DOM tree constructed.");
    });
}

window.onload = () => {
    console.log("All resources (images, styles) have finished loading.");
};`;

const navigationCode = `// Navigating the Tree: Nodes vs Elements

const item = document.querySelector('.active-item');

// NODE Navigation (Includes Text and Comment nodes)
const prevNode = item.previousSibling;
const allNodes = item.childNodes;

// ELEMENT Navigation (Ignores whitespace, much cleaner for UI logic)
const prevElement = item.previousElementSibling;
const allElements = item.children; 

// The parent relationship
const parent = item.parentElement; // Returns null if parent isn't an Element
const owner = item.ownerDocument;  // Direct access to the root document`;

const performancePatternsCode = `// Architectural Performance: Avoiding Layout Thrashing

//  ANTI-PATTERN: Forced Synchronous Layout
function updateElements(elements) {
    elements.forEach(el => {
        const width = el.offsetWidth; // 1. Read (Triggers layout calculation)
        el.style.width = \`\${width + 10}px\`; // 2. Write (Invalidates layout)
        // Repeating this in a loop causes "Layout Thrashing"
    });
}

// BEST PRACTICE: Batch Reads and Writes
const widths = elements.map(el => el.offsetWidth); // Batch Read
elements.forEach((el, i) => {
    el.style.width = \`\${widths[i] + 10}px\`; // Batch Write
});

// BEST PRACTICE: Using DocumentFragments
const fragment = document.createDocumentFragment();
for(let i=0; i<100; i++) {
    const div = document.createElement('div');
    // Fragment exists in memory, not in the DOM tree
    fragment.appendChild(div); 
}
document.body.appendChild(fragment); // Single reflow for 100 elements`;

const JSDOMIntro = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Browser Internals</div>
                <h1>The DOM Architecture & Tree Mechanics</h1>
                <p className="hero-lead">
                    Go beyond simple manipulation. Understand the Document Object Model as a
                    multi-layered architectural bridge between your static HTML and the dynamic
                    JavaScript runtime. Master the engine-level mechanics of nodes, elements,
                    and performance-critical tree traversal.
                </p>
            </div>

            <div className="content-section">
                <h2>What is the DOM? (The Objectified API)</h2>
                <p>
                    The Document Object Model (DOM) is not just a structural representation of your web page;
                    it is a cross-platform, language-independent interface that treats an HTML or XML
                    document as a living object structure. When a browser loads a page, it parses the raw
                    HTML strings and constructs a complex tree of C++ objects in memory. The DOM acts as a
                    high-level bridge, projecting these internal engine objects into the JavaScript
                    environment. This projection allows scripts to query, navigate, and mutate the
                    page's content and style in real-time. It is crucial to understand that the DOM is
                    independent of the JavaScript language itself; it is a Web API provided by the
                    browser hosting environment. Without this interface, JavaScript would be confined
                    to data processing with no way to communicate with the user's viewport.
                </p>
                <p>
                    Because the DOM is an interface to an underlying engine structure, interactions
                    between the JavaScript thread and the DOM "bridge" are relatively expensive. Each
                    time you access or modify a property like `innerHTML` or `classList`, you are making
                    a cross-context call to the browser's C++ core. This cost is negligible for a few
                    calls but becomes a major performance bottleneck in high-frequency operations or
                    animations. Senior engineers treat the DOM as a "database" rather than a local
                    variableyou should minimize your queries and mutations to maintain a responsive
                    User Experience. Understanding this architectural separation is the first step
                    toward writing efficient, industrial-grade front-end code that scales to complex
                    interactivity.
                </p>

                <CodeBlock code={domTreeVisualizationCode} language="html" />
            </div>

            <div className="content-section">
                <h2>The Hierarchy of Nodes: Beyond Elements</h2>
                <p>
                    In the DOM, everything is a **Node**. While we spent most of our time interacting
                    with `Elements` (like <code>&lt;div&gt;</code> and <code>&lt;h1&gt;</code>), the tree is actually composed of several
                    other node types that define the document's structure. These include `TextNodes`,
                    which hold the actual string content, `CommentNodes`, and the `DocumentNode` itself.
                    A common architectural pitfall is confusing the "Node" tree with the "Element" tree.
                    The Node tree contains everything, including the whitespace between tags (represented
                    as empty TextNodes), whereas the Element tree focuses purely on HTML tags. Modern
                    APIs provide separate properties for navigating these two distinct views, such as
                    `childNodes` (Nodes) versus `children` (Elements).
                </p>
                <p>
                    Each node possesses a `nodeType` property, a legacy numeric code that identifies its
                    essential nature. For instance, Element nodes are type 1, while Text nodes are type 3.
                    Distinguishing between these is vital for advanced tasks like building custom
                    rich-text editors or DOM diffing algorithms. Furthermore, while Element nodes
                    have a `tagName` property (always returning uppercase strings like "DIV"), all nodes
                    possess a `nodeName` property. For non-element nodes, this provides a special
                    identifier like `#text` or `#document`. Mastering this taxonomy allows you to traverse
                    the tree with surgical precision, ensuring your scripts interact exactly with the
                    intended structural components without being tricked by invisible whitespace or
                    internal browser markers.
                </p>

                <CodeBlock code={nodeTypesCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Global Document Object</h2>
                <p>
                    The `document` object is the entry point for all DOM-related operations. It represents
                    the entire web page loaded in the browser and serves as the root of the DOM tree.
                    Architecturally, `document` provides the factory methods needed to create new nodes,
                    the query methods needed to find existing ones, and the state properties needed to
                    monitor page progress. It also provides access to global metadata, such as the `title`,
                    `URL`, and the current character encoding. Because `document` is a singleton in
                    traditional window-based environments, it serves as the central hub where you attach
                    state listeners for critical events like `DOMContentLoaded`, which signals that
                    the basic HTML structure is ready for manipulation.
                </p>
                <p>
                    Managing the lifecycle of the `document` object is essential for performance and
                    correctness. If your script execution blocks the initial parsing of the `document`,
                    the user will see a white screena phenomenon known as "Parser Blocking." Modern
                    best practices suggest using the `async` or `defer` attributes on script tags to
                    ensure the `document` can finish its initial construction before complex logic begins.
                    Additionally, understanding the `readyState` of the document allows you to write
                    environment-agnostic initialization code that works regardless of whether the
                    script was loaded at the top or bottom of the file. By treating the `document`
                    as a managed resource, you ensure your application initializes smoothly across
                    different network speeds and device capabilities.
                </p>

                <CodeBlock code={documentPropertiesCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Tree Navigation: Semantic Traversal</h2>
                <p>
                    Navigating the DOM tree is a fundamental skill, but it requires a strategic choice
                    between Node-based and Element-based properties. **Node properties** like `parentNode`
                    and `nextSibling` are exhaustive; they see every single piece of data in the tree,
                    including comments and technical line breaks. This makes them ideal for tasks involving
                    text manipulation or internal document processing. However, for most UI-related tasks,
                    **Element properties** like `parentElement`, `children`, and `nextElementSibling`
                    are much more efficient. They automatically filter out the "noise" of TextNodes,
                    providing a direct link to the actual visual components of the page. Using the
                    correct navigation set reduces the need for manual filtering loops, resulting
                    in cleaner, more readable code.
                </p>
                <p>
                    When traversing upward through the tree, the `parentElement` property is your
                    primary tool for event delegation and finding contextual wrappers. Be aware that
                    the top-most nodes (like `html`) might return `null` for `parentElement` even though
                    they have a parent (the `document`), as the `document` is a Node but not an Element.
                    For downward traversal, the `children` collection is particularly powerful because
                    it provides a live, indexed-based view of an element's immediate descendants.
                    By combining these properties with modern query methods, you can build
                    dynamic UI behaviors that respond to the structural relationships of the
                    page, creating components that are context-aware and architecturally
                    resilient to changes in the HTML markup.
                </p>

                <CodeBlock code={navigationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Architectural Performance: Reflows & Repaints</h2>
                <p>
                    Every time you modify the DOM, you trigger a sequence of expensive operations within
                    the browser's rendering engine. When you change properties that affect geometrylike
                    `width`, `height`, or `position`the browser must perform a **Reflow** (or Layout).
                    This is a recursive calculation where the engine determines the size and position
                    of every element in the tree, often starting from the root. If you then perform a
                    visual-only change, such as `color` or `opacity`, the browser executes a **Repaint**.
                    Reflows are significantly more expensive than repaints because they require
                    multi-element calculations. High-performance engineering focuses on minimizing
                    these triggers, particularly by batching multiple mutations into a single frame to
                    prevent "Layout Thrashing."
                </p>
                <p>
                    One of the most effective patterns for minimizing reflows is the use of
                    `DocumentFragments`. A fragment is a lightweight "off-screen" document wrapper
                    that lives only in memory. You can append hundreds of nodes to a fragment without
                    triggering any browser calculations. Once your structure is complete, you
                    append the single fragment to the live DOM, triggering exactly **one** reflow
                    for the entire operation. Similarly, when reading layout properties (like `offsetWidth`),
                    do not mix them with writes (like `style.width`). This pattern, known as read-write
                    interleaving, forces the browser to recalculate the layout mid-loop, leading
                    to massive performance degradation. By understanding the rendering pipeline,
                    you transform your code from a series of "scripted changes" into an
                    orchestrated, high-performance UI engine.
                </p>

                <CodeBlock code={performancePatternsCode} language="javascript" />
            </div>

            <KeyFacts><ul>
                    <li>**Separation:** Treat the DOM as a cross-context API link, not a local variable.</li>
                    <li>**Taxonomy:** Distinguish between ELEMENT_NODEs (1) and TEXT_NODEs (3).</li>
                    <li>**Strategy:** Prefer `children` over `childNodes` for clean UI navigation.</li>
                    <li>**Lifecycle:** Use `DOMContentLoaded` to ensure the tree is constructed before manipulation.</li>
                    <li>**Efficiency:** Batch DOM writes and avoid mixing them with reads to prevent thrashing.</li>
                    <li>**Memory:** Use `DocumentFragment` for bulk insertions to minimize reflows.</li>
                    <li>**Abstraction:** Recognize that the DOM is independent of the JavaScript runtime.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDOMIntro;


